import React from 'react';
import yaml from 'js-yaml';
// renderers is imported separately so Markdoc isn't sent to the client
import Markdoc, {renderers} from '@markdoc/markdoc'

import {getSchema} from '/opt/buildhome/repo/node_modules/@markdoc/next.js/src/runtime.js';
/**
 * Schema is imported like this so end-user's code is compiled using build-in babel/webpack configs.
 * This enables typescript/ESnext support
 */
const config = {};
import * as tags from '/opt/buildhome/repo/markdoc/tags.js'
import * as nodes from '/opt/buildhome/repo/markdoc/nodes.js'
const functions = {};
const schema = {
tags: tags ? (tags.default || tags) : {},
nodes: nodes ? (nodes.default || nodes) : {},
functions: functions ? (functions.default || functions) : {},
...(config ? (config.default || config) : {}),
};

/**
 * Source will never change at runtime, so parse happens at the file root
 */
const source = "---\ntitle: Creating an AI Discord Bot\npageTitle: Creating Discord bots - Mendable\ndescription: Creating Discord bots with Mendable.\n---\n\n\nMendable provides an easy way to create a Discord bot that can answer questions about your project. This guide will show you how to create a Discord bot that uses the Mendable API to answer questions and provide sources for the answers. It creates a new thread for each question asked and stores the history of the conversation.\n\n\n## Features\n\n- Starts a new thread for each question asked.\n- Uses the Mendable API to answer questions and provide sources.\n- Stores the history of the conversation.\n\n## Setup\n\n1. Clone [Mendable AI Discord Bot](https://github.com/mendableai/ai-discord-bot) repository.\n2. Install the required dependencies with `npm install`.\n3. Create a `.env` file in the root directory of the project and add the following environment variables:\n    - `MENDABLE_API_KEY`: Your Mendable API key.\n    - `DISCORD_TOKEN`: Your Discord [bot token](#creating-a-discord-application-and-getting-the-necessary-tokens).\n    - `BOT_ID`: Your Discord [bot ID](#creating-a-discord-application-and-getting-the-necessary-tokens).\n4. Run the bot with `node index.js` or `npm run start`.\n\n## Usage\n\nTo ask a question, mention the bot followed by your question. For example:\n\n```\n@MendableAI How to create a Mendable AI Discord Bot?\n```\n\nThe bot will create a new thread, answer the question, and provide sources for the answer.\n\n## Dependencies\n\n- `discord.js`: A powerful library for interacting with the Discord API.\n- `dotenv`: A zero-dependency module that loads environment variables from a `.env` file into `process.env`.\n\n\n## Creating a Discord Application and Getting the Necessary Tokens\n\n1. **Create a Discord Application**\n    - Go to the [Discord Developer Portal](https://discord.com/developers/applications).\n    - Click on the `New Application` button.\n    - Give your application a name and click `Create`.\n\n2. **Create a Bot for the Application**\n    - Click on the `Bot` tab on the left side of the application page.\n    - Click on the `Add Bot` button on the right and confirm by clicking `Yes, do it!`.\n\n3. **Get the Bot Token**\n    - Still on the `Bot` tab, find the `Token` section.\n    - Click on `Copy` to copy the bot token. This is your `DISCORD_TOKEN`.\n\n5. **Get the Bot ID**\n    - Go to the `General Information` tab on the left side of the application page.\n    - Find the `Application ID` section and click `Copy`. This is your `BOT_ID`.\n\n6. **Invite the Bot to Your Server**\n    - Go to the `OAuth2` tab on the left side of the application page.\n    - In the `Scopes` section, select `bot`.\n    - In the `Bot Permissions` section, select the permissions your bot needs.\n    - Copy the generated URL and open it in your web browser to invite the bot to your server.\n\n5. **Get the Bot ID**\n    - Right click on the bot name and select `Copy ID`. This is your `BOT_ID`.\n\n\n\n";
const filepath = "/integrations/discord.md";
const ast = Markdoc.parse(source);

/**
 * Like the AST, frontmatter won't change at runtime, so it is loaded at file root.
 * This unblocks future features, such a per-page dataFetchingFunction.
 */
const frontmatter = ast.attributes.frontmatter
  ? yaml.load(ast.attributes.frontmatter)
  : {};

const {components, ...rest} = getSchema(schema)

export async function getStaticProps(context) {
  const partials = {};

  // Ensure Node.transformChildren is available
  Object.keys(partials).forEach((key) => {
    partials[key] = Markdoc.parse(partials[key]);
  });

  const cfg = {
    ...rest,
    variables: {
      ...(rest ? rest.variables : {}),
      // user can't override this namespace
      markdoc: {frontmatter},
      // Allows users to eject from Markdoc rendering and pass in dynamic variables via getServerSideProps
      ...(context.variables || {})
    },
    partials,
    source,
  };

  /**
   * transform must be called in dataFetchingFunction to support server-side rendering while
   * accessing variables on the server
   */
  const content = Markdoc.transform(ast, cfg);

  return {
    // Removes undefined
    props: JSON.parse(
      JSON.stringify({
        markdoc: {
          content,
          frontmatter,
          file: {
            path: filepath
          }
        },
      })
    ),
  };
}

export default function MarkdocComponent(props) {
  // Only execute HMR code in development
  return renderers.react(props.markdoc.content, React, {
    components: {
      ...components,
      // Allows users to override default components at runtime, via their _app
      ...props.components,
    },
  });
}
